import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const steps = [
  {
    num: '01',
    title: 'Case Evaluation',
    desc: 'Thorough review of your facts, risks, and legal position. We identify every angle before making a single move.',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Strategy Development',
    desc: 'A clear, customized plan designed to maximize your leverage, minimize exposure, and optimize your outcome.',
    icon: 'ri-mind-map',
  },
  {
    num: '03',
    title: 'Execution',
    desc: 'Negotiations, filings, and litigation handled with precision and urgency. No delays. No half-measures.',
    icon: 'ri-focus-3-line',
  },
  {
    num: '04',
    title: 'Resolution',
    desc: 'Relentless focus on the strongest possible result — whether through settlement or courtroom victory.',
    icon: 'ri-trophy-line',
  },
];

export default function Process() {
  const { ref, inView } = useInView();

  return (
    <section id="process" className="bg-[#0D0D0D] py-24 md:py-32 relative overflow-hidden" ref={ref}>
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      ></div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className={`mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.4em] uppercase font-medium">How We Work</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-serif text-white text-4xl md:text-5xl font-bold leading-tight max-w-lg">
              A Strategic Approach<br />to Every Case
            </h2>
            <p className="text-white/40 text-sm max-w-xs leading-relaxed md:text-right">
              Every matter follows a disciplined, results-oriented process built to protect your position from day one.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative group transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-[38px] left-[calc(50%+38px)] right-0 h-[1px] bg-gradient-to-r from-[#C9A84C]/40 to-[#C9A84C]/10 z-10"></div>
              )}

              <div className="p-8 border border-white/5 group-hover:border-[#C9A84C]/30 transition-all duration-300 h-full">
                {/* Number + icon */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-[76px] h-[76px] rounded-full border border-[#C9A84C]/30 group-hover:border-[#C9A84C] flex items-center justify-center transition-all duration-300 bg-[#111]">
                      <i className={`${step.icon} text-[#C9A84C] text-2xl`}></i>
                    </div>
                    <span className="absolute -top-2 -right-3 font-serif text-[#C9A84C]/20 text-3xl font-bold leading-none group-hover:text-[#C9A84C]/40 transition-colors duration-300">
                      {step.num}
                    </span>
                  </div>
                </div>

                <h3 className="font-serif text-white text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{step.desc}</p>

                {/* Gold bottom accent */}
                <div className="mt-6 w-0 h-[2px] bg-[#C9A84C] group-hover:w-12 transition-all duration-400"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
